import { FC, useRef, useState, type DragEvent } from "react";
import { useTranslation } from "react-i18next";
import { message } from "antd";
import { ArrowLeft, ArrowRight, CircleCheck, CloudUpload, X } from "lucide-react";
import DefinitionTooltip from "src/components/common/DefinitionTooltip/DefinitionTooltip";
import StyledDropdown from "src/components/reusable/StyledDropdown";
import StyledTextInput from "src/components/reusable/StyledTextInput";
import StyledTextArea from "src/components/reusable/StyledTextArea";
import CircularLoader from "src/components/reusable/CircularLoader/CircularLoader";
import { submitBugReport, submitFeatureRequest } from "src/services/FeedbackAPIs";
import { usePageOptions } from "../Hooks/UsePageOptions";
import { getSubmitErrorMessage } from "../Utils/HelpSupportHelpers";

const MAX_FILES = 10;
const MAX_TOTAL_BYTES = 100 * 1024 * 1024;
const MAX_DESCRIPTION_CHARS = 5000;

const isAcceptedFile = (file: File) =>
  file.type.startsWith("image/") || file.type.startsWith("video/");

const isValidHttpUrl = (value: string) => {
  try {
    const parsed = new URL(value);
    return parsed.protocol === "http:" || parsed.protocol === "https:";
  } catch {
    return false;
  }
};

interface PageFeedbackFormProps {
  /** i18n key prefix under `helpSupport.` holding this form's copy (e.g. "reportBug"). */
  namespace: "reportBug" | "featureRequest";
}

const PageFeedbackForm: FC<PageFeedbackFormProps> = ({ namespace }) => {
  const { t, i18n } = useTranslation();
  const isArabic = i18n.language?.startsWith("ar");
  const SubmitArrowIcon = isArabic ? ArrowLeft : ArrowRight;
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const { pageOptions, getSubpageOptions } = usePageOptions();

  const [url, setUrl] = useState<string>("");
  // Shown as a greyed-out example only — the field is optional and starts empty.
  const currentUrl = window.location.href;
  const [selectedPage, setSelectedPage] = useState<string | undefined>(undefined);
  const [selectedSubpage, setSelectedSubpage] = useState<string | undefined>(undefined);
  const [description, setDescription] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const subpageOptions = getSubpageOptions(selectedPage);
  const hasSubpages = subpageOptions.length > 0;

  const descriptionLength = description.length;
  const isDescriptionAtLimit = descriptionLength === MAX_DESCRIPTION_CHARS;
  const isDescriptionOverLimit = descriptionLength > MAX_DESCRIPTION_CHARS;

  const addFiles = (incoming: FileList | File[]) => {
    const accepted: File[] = [];
    let runningCount = files.length;
    let runningBytes = files.reduce((total, file) => total + file.size, 0);

    for (const file of Array.from(incoming)) {
      if (!isAcceptedFile(file)) {
        message.warning(t(`helpSupport.${namespace}.invalidType`, { name: file.name }), 5);
        continue;
      }
      if (runningCount >= MAX_FILES) {
        message.warning(t(`helpSupport.${namespace}.tooManyFiles`, { max: MAX_FILES }), 5);
        break;
      }
      if (runningBytes + file.size > MAX_TOTAL_BYTES) {
        message.warning(t(`helpSupport.${namespace}.totalTooLarge`), 5);
        break;
      }
      accepted.push(file);
      runningCount += 1;
      runningBytes += file.size;
    }

    if (accepted.length > 0) {
      setFiles(prev => [...prev, ...accepted]);
      accepted.forEach(file => {
        message.success(t(`helpSupport.${namespace}.fileUploaded`, { name: file.name }), 5);
      });
    }
  };

  const handleDrop = (event: DragEvent<HTMLButtonElement>) => {
    event.preventDefault();
    setIsDragging(false);
    if (event.dataTransfer.files?.length) {
      addFiles(event.dataTransfer.files);
    }
  };

  const removeFile = (index: number) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handlePageChange = (value: string) => {
    setSelectedPage(value);
    setSelectedSubpage(undefined);
  };

  const handleSubmit = async () => {
    const trimmedUrl = url.trim();
    if (!trimmedUrl) {
      message.error(t("helpSupport.urlRequired"), 5);
      return;
    }
    if (!isValidHttpUrl(trimmedUrl)) {
      message.error(t("helpSupport.urlInvalid"), 5);
      return;
    }
    if (!selectedPage) {
      message.error(t("helpSupport.pageRequired"), 5);
      return;
    }
    if (hasSubpages && !selectedSubpage) {
      message.error(t("helpSupport.subpageRequired"), 5);
      return;
    }
    if (!description.trim()) {
      message.error(t(`helpSupport.${namespace}.descriptionRequired`), 5);
      return;
    }
    if (isDescriptionOverLimit) {
      message.error(t("helpSupport.charLimitExceeded", { max: MAX_DESCRIPTION_CHARS }), 5);
      return;
    }
    if (files.length === 0) {
      message.error(t(`helpSupport.${namespace}.imageRequired`), 5);
      return;
    }
    setIsSubmitting(true);
    try {
      const submitFn = namespace === "reportBug" ? submitBugReport : submitFeatureRequest;
      await submitFn(
        description.trim(),
        files,
        selectedPage,
        selectedSubpage,
        trimmedUrl
      );
      message.success(t(`helpSupport.${namespace}.submitted`), 5);
      setSelectedPage(undefined);
      setSelectedSubpage(undefined);
      setDescription("");
      setFiles([]);
      setUrl("");
    } catch (error: unknown) {
      message.error(getSubmitErrorMessage(error, t(`helpSupport.${namespace}.submitError`)), 5);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <div className="help-support-field">
        <div className="help-support-field__label-row">
          <label
            className="help-support-field__label"
            htmlFor={`help-support-${namespace}-url`}
          >
            {t("helpSupport.urlLabel")}
            <span className="help-support-field__required" aria-hidden>*</span>
          </label>
          <DefinitionTooltip
            label={t("helpSupport.urlLabel")}
            info={{ description: t("helpSupport.urlHint") }}
          />
        </div>
        <StyledTextInput
          id={`help-support-${namespace}-url`}
          type="url"
          className="help-support-input"
          placeholder={`${t("helpSupport.urlExamplePrefix")} ${currentUrl}`}
          value={url}
          onChange={event => setUrl(event.target.value)}
        />
      </div>

      <div className="help-support-row">
        <div className={`help-support-field ${!hasSubpages ? "help-support-field--half" : ""}`}>
          <label className="help-support-field__label">
            {t("helpSupport.selectPage")}
            <span className="help-support-field__required" aria-hidden>*</span>
          </label>
          <StyledDropdown
            className="help-support-select"
            placeholder={t("helpSupport.choosePage")}
            value={selectedPage}
            onChange={value => handlePageChange(value as string)}
            options={pageOptions}
          />
        </div>

        {selectedPage && hasSubpages ? (
          <div className="help-support-field">
            <label className="help-support-field__label">
              {t("helpSupport.selectSubpage")}
              <span className="help-support-field__required" aria-hidden>*</span>
            </label>
            <StyledDropdown
              className="help-support-select"
              placeholder={t("helpSupport.chooseSubpage")}
              value={selectedSubpage}
              onChange={value => setSelectedSubpage(value as string)}
              options={subpageOptions}
            />
          </div>
        ) : null}
      </div>

      <div className="help-support-field">
        <label
          className="help-support-field__label"
          htmlFor={`help-support-${namespace}-description`}
        >
          {t(`helpSupport.${namespace}.describe`)}
          <span className="help-support-field__required" aria-hidden>*</span>
        </label>
        <StyledTextArea
          id={`help-support-${namespace}-description`}
          className={`help-support-textarea ${
            isDescriptionOverLimit ? "help-support-textarea--error" : ""
          }`}
          rows={4}
          placeholder={t(`helpSupport.${namespace}.describePlaceholder`)}
          value={description}
          onChange={event => setDescription(event.target.value)}
          aria-invalid={isDescriptionOverLimit}
          aria-describedby={`help-support-${namespace}-description-count`}
        />
        <div
          id={`help-support-${namespace}-description-count`}
          className="help-support-charcount-row"
          aria-live="polite"
        >
          <span
            className={`help-support-charcount ${
              isDescriptionOverLimit || isDescriptionAtLimit
                ? "help-support-charcount--error"
                : ""
            }`}
          >
            {t("helpSupport.charCount", {
              current: descriptionLength,
              max: MAX_DESCRIPTION_CHARS,
            })}
          </span>
          {isDescriptionOverLimit ? (
            <span className="help-support-charcount--error">
              {t("helpSupport.charLimitExceeded", { max: MAX_DESCRIPTION_CHARS })}
            </span>
          ) : null}
          {isDescriptionAtLimit ? (
            <span className="help-support-charcount--error">
              {t("helpSupport.charLimitReached", { max: MAX_DESCRIPTION_CHARS })}
            </span>
          ) : null}
        </div>
      </div>

      <div className="help-support-field">
        <label className="help-support-field__label">
          {t(`helpSupport.${namespace}.attach`)}
          <span className="help-support-field__required" aria-hidden>*</span>
        </label>
        <button
          type="button"
          className={`help-support-dropzone ${
            isDragging ? "help-support-dropzone--active" : ""
          }`}
          onDragOver={event => {
            event.preventDefault();
            setIsDragging(true);
          }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
        >
          <CloudUpload size={26} className="help-support-dropzone__icon" aria-hidden />
          <span className="help-support-dropzone__text">
            {t(`helpSupport.${namespace}.dragDrop`)}
            <span className="help-support-dropzone__browse">
              {t(`helpSupport.${namespace}.browse`)}
            </span>
          </span>
          <span className="help-support-dropzone__hint">
            {t(`helpSupport.${namespace}.fileHint`)}
          </span>
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*,video/*"
          multiple
          className="help-support-dropzone__input"
          onChange={event => {
            if (event.target.files?.length) {
              addFiles(event.target.files);
            }
            event.target.value = "";
          }}
        />

        {files.length > 0 ? (
          <ul className="help-support-files">
            {files.map((file, index) => (
              <li key={`${file.name}-${index}`} className="help-support-files__item">
                <span className="help-support-files__name-wrap">
                  <CircleCheck size={16} className="help-support-files__check" aria-hidden />
                  <span className="help-support-files__name">{file.name}</span>
                </span>
                <button
                  type="button"
                  className="help-support-files__remove"
                  aria-label={t(`helpSupport.${namespace}.removeFile`)}
                  onClick={() => removeFile(index)}
                >
                  <X size={14} />
                </button>
              </li>
            ))}
          </ul>
        ) : null}
      </div>

      <div className="help-support-submit-row">
        <button
          type="button"
          className="help-support-submit"
          onClick={handleSubmit}
          disabled={isSubmitting || isDescriptionOverLimit}
        >
          {t(`helpSupport.${namespace}.submit`)}
          <SubmitArrowIcon size={15} aria-hidden />
        </button>
        {isSubmitting ? (
          <span className="help-support-submit-spinner">
            <CircularLoader size={22} thickness={3} />
          </span>
        ) : null}
      </div>
    </>
  );
};

export default PageFeedbackForm;
